import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Edit as EditIcon,
  PlayArrow as StartJobIcon,
  Work as JobDetailIcon,
  List as StepsIcon,
  Settings as JobSettingsIcon,
  Mail as MailSettingsIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { usePermissions } from '../api/auth/PermissionsContext';
import { SAPButton } from '../components/design-system';
import { SAP_COLORS, SAP_TYPOGRAPHY } from '../theme/sapDesignSystem';
import JobDetailData, { JobDetailDataRef } from './JobDetailData';
import JobSetting from './JobSetting';
import MailSettingsPanel from './MailSetting';
import { api } from '../api/fetchWrapper';
import { getEndpoint, API_ENDPOINTS } from '../api/apiConstants';
import StepDetails from './StepDetails';

// Types
interface JobDetailData {
  id: number;
  jobName: string;
  jobCode: string;
  status: 'RUNNING' | 'STOPPED' | 'PAUSED';
  interval: number;
  lastStartTime: number;
  lastScheduledStartTime: number;
  lastEndTime: number;
  nextRunTime: number;
  retryEnabled: boolean;
  isAutoRetryEnabled: boolean;
  intervalType: string;
  timeIntervalType: string;
  agentJob: boolean;
  lastConfiguredBy: string;
  creationDate: number;
  lastUpdateDate: number;
  // Optional metadata used for updates
  clientId?: number;
  createdBy?: number;
  lastUpdatedBy?: number;
  version?: number;
  toDateTime?: number | null;
  maxRetryCount?: number | null;
}

interface JobDetailEditProps {
  clientCode?: string;
}

// Tab configuration with permission (functionName) required to see each tab
// JOB_DETAIL_EDIT = job detail tab view, edit | ALLOW_STEP_EDIT = step tab | ALLOW_JOB_SETTINGS_EDIT = job setting tab | MAIL = mail screen view
const TAB_CONFIG = [
  { id: 'job-detail', label: 'Job Detail', icon: JobDetailIcon, permission: 'JOB_DETAIL_EDIT' },
  { id: 'steps', label: 'Steps', icon: StepsIcon, permission: 'ALLOW_STEP_EDIT' },
  { id: 'job-settings', label: 'Job Settings', icon: JobSettingsIcon, permission: 'ALLOW_JOB_SETTINGS_EDIT' },
  { id: 'mail-settings', label: 'Mail Settings', icon: MailSettingsIcon, permission: 'MAIL' },
];

const JobDetailEdit: React.FC<JobDetailEditProps> = ({ clientCode: clientCodeProp }) => {
  const { id } = useParams<{ id: string }>();
  const { clientCode: clientCodeFromContext } = useClientCode();
  const { permissions } = usePermissions();
  const clientCode = clientCodeProp ?? clientCodeFromContext ?? 'admin';
  const jobId = id;
  const navigate = useNavigate();
  const location = useLocation();

  // JOB_DETAIL_EDIT: allow Save + Start Job actions on Job Detail tab
  const hasTriggerJob = permissions.includes('JOB_DETAIL_EDIT');
  // Tab edit permissions from API (functionName)
  // ALLOW_STEP_EDIT = step tab view, edit, create, delete; STEP_EDIT = step edit sub-tabs (step detail, settings, request validator)
  const allowStepEdit = permissions.includes('ALLOW_STEP_EDIT');
  const allowStepDetailEdit = permissions.includes('STEP_EDIT');
  const allowJobSettingsEdit = permissions.includes('ALLOW_JOB_SETTINGS_EDIT');
  const allowMailSettingsEdit = permissions.includes('MAIL');

  // Tabs the user is allowed to see (by functionName from API)
  const visibleTabs = React.useMemo(
    () => TAB_CONFIG.filter((tab) => permissions.includes(tab.permission)),
    [permissions]
  );
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const activeTabId = visibleTabs[Math.min(activeTabIndex, Math.max(0, visibleTabs.length - 1))]?.id ?? 'job-detail';

  useEffect(() => {
    if (activeTabIndex >= visibleTabs.length && visibleTabs.length > 0) {
      setActiveTabIndex(visibleTabs.length - 1);
    }
  }, [visibleTabs.length, activeTabIndex]);
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startJobDialogOpen, setStartJobDialogOpen] = useState(false);
  const [startingJob, setStartingJob] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [pendingSaveChanges, setPendingSaveChanges] = useState<string[]>([]);

  const jobDataRef = React.useRef<JobDetailDataRef>(null);

  // No data fetching in this layout component anymore

  const [displayJobName, setDisplayJobName] = useState<string>(jobId ? `Job ${jobId}` : 'Job undefined');

  // Ensure breadcrumb shows actual job name across all tabs
  useEffect(() => {
    const loadJobName = async () => {
      if (!jobId) return;
      try {
        const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOB_DETAIL_EDIT.GET_BY_ID.replace('{id}', String(jobId)));
        const res: any = await api.get(endpoint);
        if (res?.jobName) {
          setDisplayJobName(res.jobName);
        }
      } catch {
        // ignore, leave fallback label
      }
    };
    loadJobName();
  }, [clientCode, jobId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
  };

  const handleStartJob = () => {
    setStartJobDialogOpen(true);
  };

  const confirmStartJob = async () => {
    if (!jobId) return;
    try {
      setStartingJob(true);
      await jobDataRef.current?.startJob();
      setStartJobDialogOpen(false);
    } catch (err) {
      logger.error('Error starting job:', err);
      setError('Failed to start job. Please try again.');
    } finally {
      setStartingJob(false);
    }
  };

  const cancelStartJob = () => {
    setStartJobDialogOpen(false);
  };

  const handleToggleEdit = () => {};

  const handleSaveClick = () => {
    const changes = jobDataRef.current?.getChangesSummary?.() ?? [];
    if (changes.length === 0) {
      setPendingSaveChanges([]);
      setSaveConfirmOpen(true);
      return;
    }
    setPendingSaveChanges(changes);
    setSaveConfirmOpen(true);
  };

  const handleSaveConfirm = async () => {
    if (!jobId) return;
    setSaveConfirmOpen(false);
    try {
      await jobDataRef.current?.save();
      setEditMode(false);
      setPendingSaveChanges([]);
    } catch (e) {
      logger.error('Save failed', e);
      setError('Failed to save changes.');
    }
  };

  const handleSaveCancel = () => {
    setSaveConfirmOpen(false);
    setPendingSaveChanges([]);
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="400px"
          sx={{
            backgroundColor: SAP_COLORS.sapWhite,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ 
              color: SAP_COLORS.sapBlue,
              mb: 3 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: SAP_COLORS.sapGray7,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              mb: 1
            }}
          >
            Loading job details...
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: SAP_COLORS.sapGray5,
              textAlign: 'center',
              maxWidth: 300
            }}
          >
            Fetching information for Job ID: {jobId}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box 
          sx={{
            backgroundColor: SAP_COLORS.sapWhite,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            p: 4,
          }}
        >
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: '1rem',
                fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              }
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
              Failed to Load Job Details
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Job ID: {jobId} | Client: {clientCode}
            </Typography>
          </Alert>
          <Box display="flex" gap={2}>
            <SAPButton
              variant="primary"
              onClick={() => jobDataRef.current?.save?.()}
              disabled={loading}
            >
              Retry
            </SAPButton>
            <SAPButton
              variant="secondary"
              onClick={() => navigate('/job-detail')}
            >
              Back to Jobs
            </SAPButton>
          </Box>
        </Box>
      );
    }

    // In layout-only mode, defer empty state to child
    if (false) {
      return (
        <Alert severity="warning">
          Job not found or no data available.
        </Alert>
      );
    }

    switch (activeTabId) {
      case 'job-detail':
        return (
          <JobDetailData
            ref={jobDataRef}
            clientCode={clientCode}
            jobId={jobId!}
            onJobNameChange={setDisplayJobName}
          />
        );

      case 'steps':
        return (
          <StepDetails allowStepEdit={allowStepEdit} allowStepDetailEdit={allowStepDetailEdit} />
        );

      case 'job-settings':
        return (
          <JobSetting 
            clientCode={clientCode} 
            jobDetailId={jobId!} 
            clientId={jobDataRef.current?.getClientId?.() ?? 0}
            allowEdit={allowJobSettingsEdit}
          />
        );

      case 'mail-settings':
        return (
          <MailSettingsPanel 
            clientCode={clientCode} 
            jobDetailId={jobId!} 
            clientId={jobDataRef.current?.getClientId?.() ?? 0}
            allowEdit={allowMailSettingsEdit}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ backgroundColor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Top Bar: Breadcrumb + Actions */}
      <Box
        sx={{
          backgroundColor: SAP_COLORS.sapWhite,
          color: SAP_COLORS.sapGray7,
          px: 3,
          py: 2,
          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Breadcrumbs
              separator="›"
              sx={{
                color: SAP_COLORS.sapGray7,
                '& .MuiBreadcrumbs-separator': { color: SAP_COLORS.sapGray5 },
              }}
            >
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  const fromListSearch = (location.state as { fromListSearch?: string })?.fromListSearch;
                  if (fromListSearch) {
                    navigate(fromListSearch);
                    return;
                  }
                  if (window.history.length > 1) {
                    navigate(-1);
                    return;
                  }
                  navigate('/job-details');
                }}
                sx={{
                  color: SAP_COLORS.sapBlue,
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Jobs
              </Link>
              <Typography color={SAP_COLORS.sapGray7} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
                {displayJobName}
              </Typography>
            </Breadcrumbs>
          </Box>

          <Box display="flex" gap={2}>
            {/* Save and Start Job only on Job Detail tab and only when user has TRIGGER_JOB */}
            {activeTabId === 'job-detail' && hasTriggerJob && (
              <Tooltip title="Save Job Details">
                <span>
                  <IconButton
                    onClick={handleSaveClick}
                    disabled={loading}
                    sx={{
                      color: SAP_COLORS.sapBlue,
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {activeTabId === 'job-detail' && hasTriggerJob && (
              <SAPButton
                variant="success"
                startIcon={<StartJobIcon />}
                disabled={loading || startingJob}
                onClick={handleStartJob}
              >
                {startingJob ? 'Starting...' : 'Start Job'}
              </SAPButton>
            )}
            
            {/* Edit removed: always editable in child component */}
          </Box>
        </Box>
      </Box>

      {/* Tabs Section (sticky, blends with header) */}
      <Box
        sx={{
          backgroundColor: SAP_COLORS.sapWhite,
          px: { xs: 2, sm: 3 },
          position: 'sticky',
          top: 64, // below top bar
          zIndex: 9,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Tabs
          value={visibleTabs.length === 0 ? 0 : Math.min(activeTabIndex, visibleTabs.length - 1)}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: { xs: 44, sm: 48 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: { xs: 44, sm: 48 },
              color: SAP_COLORS.sapGray5,
              flex: 1,
              minWidth: 0,
              '&.Mui-selected': {
                color: SAP_COLORS.sapBlue,
              },
            },
            '& .MuiTabs-flexContainer': {
              display: 'flex',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: SAP_COLORS.sapBlue,
              height: 3,
            },
          }}
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.label}
              icon={<tab.icon />}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Main Content Area */}
      <Box 
        sx={{
          p: { xs: 2, sm: 3 },
          mt: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 'calc(100vh - 220px)',
        }}
      >
        {renderTabContent()}
      </Box>

      {/* Start Job Confirmation Dialog */}
      <Dialog
        open={startJobDialogOpen}
        onClose={cancelStartJob}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: SAP_COLORS.sapBlue, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
          Confirm Job Start
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            Are you sure you want to start the job <strong>"{displayJobName}"</strong>?
            <br />
            <br />
            This action will immediately begin execution of the job. If next run time is in the past, the job may run automatically shortly—refresh in 2–3 minutes to see status.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={cancelStartJob}
            disabled={startingJob}
            sx={{
              color: SAP_COLORS.sapGray5,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
            }}
          >
            Cancel
          </Button>
          <SAPButton
            variant="success"
            onClick={confirmStartJob}
            disabled={startingJob}
            startIcon={startingJob ? <CircularProgress size={16} color="inherit" /> : <StartJobIcon />}
          >
            {startingJob ? 'Starting...' : 'Start Job'}
          </SAPButton>
        </DialogActions>
      </Dialog>

      {/* Save Job Detail confirmation – show what changed */}
      <Dialog
        open={saveConfirmOpen}
        onClose={handleSaveCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: SAP_COLORS.sapBlue, fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold }}>
          Confirm save – Job Detail section
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1rem', lineHeight: 1.6 }}>
            {pendingSaveChanges.length === 0 ? (
              <>No changes detected in the Job Detail section. Do you still want to save?</>
            ) : (
              <>
                You have made the following changes in the Job Detail section:
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {pendingSaveChanges.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </Box>
                Do you want to save these changes?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleSaveCancel}
            sx={{
              color: SAP_COLORS.sapGray5,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
            }}
          >
            Cancel
          </Button>
          <SAPButton variant="primary" onClick={handleSaveConfirm}>
            Save
          </SAPButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobDetailEdit;